import { Check, RefreshCcw, AlertTriangle } from "lucide-react";
import { ChapterHead, Reveal } from "./motion";
import { OPTION, ANNUAL_COSTS, MILESTONE_PCTS, MILESTONE_NAMES, MILESTONE_DUES, fmt } from "./data";

export const Investment = () => {
  const rows = MILESTONE_PCTS.map((pct, i) => ({
    name: MILESTONE_NAMES[i],
    pct,
    total: Math.round((OPTION.base * pct) / 100),
    due: MILESTONE_DUES[i],
  }));
  const annualTotal = ANNUAL_COSTS.reduce((s, c) => s + c.aed, 0) + OPTION.support;

  return (
    <section id="investment" data-testid="chapter-investment" className="chapter-print-break bg-alt py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ChapterHead
          num="06"
          title="Investment"
          sub="Fixed-price consultancy engagement. No hidden fees, no surprises."
        />

        <Reveal>
          <div className="relative rounded-sm border border-amber-500 bg-elevated p-8 sm:p-12">
            <p className="font-mono2 text-[11px] tracking-[0.3em] text-accent uppercase">{OPTION.name}</p>
            <p className="mt-1 text-sm text-ink5">{OPTION.tag}</p>
            <div className="mt-6 flex flex-wrap items-baseline gap-x-5 gap-y-2">
              <p className="font-display text-lg font-medium text-ink5 line-through">
                AED {fmt(OPTION.originalBase)}
              </p>
              <p data-testid="investment-total" className="font-display text-5xl font-extrabold text-accent sm:text-6xl">
                AED {fmt(OPTION.total)}
              </p>
              <span className="rounded-full bg-amber-500 px-4 py-1.5 text-xs font-bold tracking-wider text-[#0b0f17] uppercase">
                Special rate — save AED {fmt(OPTION.originalBase - OPTION.total)}
              </span>
            </div>
            <p className="mt-3 text-sm text-ink4">{OPTION.vatNote}</p>
            <ul className="mt-8 grid gap-x-10 gap-y-3 sm:grid-cols-2">
              {OPTION.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-ink3">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.05} className="mt-8">
          <div data-testid="exclusion-alert" className="flex items-start gap-4 rounded-sm border-2 border-amber-500 bg-amber-500/10 px-6 py-5">
            <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-amber-500" />
            <div>
              <p className="font-display text-base font-bold text-ink">
                Not included in the implementation fee
              </p>
              <p className="mt-1 text-sm leading-relaxed text-ink3">
                The AED {fmt(OPTION.total)} covers implementation services only. The Odoo Enterprise
                license (AED {fmt(ANNUAL_COSTS[1].aed)}/year) and odoo.sh hosting (AED{" "}
                {fmt(ANNUAL_COSTS[0].aed)}/year) are <strong className="text-ink">not included — not in
                the first year, not in any year</strong> — and are paid directly to Odoo from the first year onwards.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-16">
          <h3 className="font-display text-xl font-semibold text-ink">Milestone Payment Schedule</h3>
          <div className="mt-6 overflow-x-auto rounded-sm border border-line">
            <table data-testid="milestone-table" className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-b border-line bg-surface">
                  {["Milestone", "%", "Amount", "Due"].map((h) => (
                    <th key={h} className="font-mono2 px-6 py-4 text-[11px] font-medium tracking-[0.25em] text-ink5 uppercase">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((m, i) => (
                  <tr
                    key={m.name}
                    data-testid={`milestone-row-${i}`}
                    className="border-b border-line transition-colors duration-200 last:border-0 hover:bg-surface"
                  >
                    <td className="px-6 py-5 text-sm font-medium text-ink">{m.name}</td>
                    <td className="px-6 py-5">
                      <span className="rounded-full border border-amber-500/40 px-3 py-1 text-xs font-semibold text-accent">
                        {m.pct}%
                      </span>
                    </td>
                    <td className="font-display px-6 py-5 text-base font-semibold text-ink2">
                      AED {fmt(m.total)}
                    </td>
                    <td className="px-6 py-5 text-sm text-ink4">{m.due}</td>
                  </tr>
                ))}
                <tr className="bg-elevated">
                  <td className="px-6 py-5 text-sm font-bold text-ink">Total Contract Value</td>
                  <td className="px-6 py-5 text-sm font-semibold text-accent">100%</td>
                  <td className="font-display px-6 py-5 text-lg font-extrabold text-accent">
                    AED {fmt(OPTION.total)}
                  </td>
                  <td className="px-6 py-5 text-sm text-ink5">No VAT</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-16">
          <div className="flex items-center gap-3">
            <RefreshCcw className="h-5 w-5 text-amber-500" />
            <h3 className="font-display text-xl font-semibold text-ink">Annual Recurring Costs</h3>
          </div>
          <p className="mt-2 text-sm text-ink5">
            Not included in the implementation fee — payable annually, and the hosting and license are paid directly to Odoo.
          </p>
          <div className="mt-6 grid gap-px overflow-hidden rounded-sm border border-line bg-line sm:grid-cols-3">
            {ANNUAL_COSTS.map((c) => (
              <div key={c.name} data-testid={`annual-cost-${c.name.includes("Hosting") ? "hosting" : "license"}`} className="bg-surface p-7">
                <p className="font-mono2 text-[10px] tracking-[0.25em] text-ink5 uppercase">{c.name}</p>
                <p className="font-display mt-3 text-2xl font-bold text-ink">
                  AED {fmt(c.aed)} <span className="text-sm font-medium text-ink5">/ year</span>
                </p>
                <p className="mt-1 text-sm text-accent">≈ USD {c.usd}</p>
                <p className="mt-3 text-xs leading-relaxed text-ink5">{c.note}</p>
              </div>
            ))}
            <div data-testid="annual-cost-support" className="bg-surface p-7">
              <p className="font-mono2 text-[10px] tracking-[0.25em] text-ink5 uppercase">Annual Support</p>
              <p className="font-display mt-3 text-2xl font-bold text-ink">
                AED {fmt(OPTION.support)} <span className="text-sm font-medium text-ink5">/ year</span>
              </p>
              <p className="mt-1 text-sm text-accent">20% of the contract value</p>
              <p className="mt-3 text-xs leading-relaxed text-ink5">
                Ongoing functional support, minor enhancements and platform guidance, billed from Go-Live.
              </p>
            </div>
          </div>
          <div className="mt-px flex items-center justify-between rounded-b-sm border border-t-0 border-line bg-elevated px-7 py-5">
            <p className="font-mono2 text-[11px] tracking-[0.25em] text-ink4 uppercase">Total Annual</p>
            <p data-testid="annual-total" className="font-display text-xl font-extrabold text-accent">
              AED {fmt(annualTotal)} / year
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
